/* eslint-env browser */

/**
 * Singleton to fetch all doc statuses in a single request, reducing the number of requests being made
 */
class StatusCache {
  static #CHECK_INTERVAL = 5000
  static #cachedJobs = []
  static #lastCheck = 0

  static async checkStatus (jobId) {
    // If the cache is stale, request job data again
    if (Date.now() > this.#lastCheck + this.#CHECK_INTERVAL) {
      this.#lastCheck = Date.now()
      try {
        const response = await fetch('/api/jobs')
        if (!response.ok) throw new Error('Network error')
        this.#cachedJobs = await response.json()
      } catch (err) {
        this.#lastCheck = 0
        console.error('Status cache error:', err)
      }
    }

    const job = this.#cachedJobs.find(job => job.id === jobId)
    return job
  }

  /**
   * Reset the cache. Intended for testing.
   */
  static reset () {
    this.#cachedJobs = []
    this.#lastCheck = 0
  }
}

/**
 * Web component for monitoring and displaying the status of a job (AI extraction task).
 *
 * This polls for job status, updates the tag,
 * and announces status changes using an aria-live region.
 *
 * Attributes:
 * - job-id:     The ID of the job to check.
 * - job-title:  The title of the job being displayed, used in the accessible status updates.
 *
 * Expects to find a Govuk Tag as a child, which it colors and updates with the current status.
 *
 * Example usage:
 *   <status-checker job-id="1234" job-title="Article 4 Direction" status="For review">
 *     <strong class="govuk-tag govuk-tag--grey">queued</strong>
 *   </status-checker>
 */
class StatusChecker extends HTMLElement {
  #CHECK_INTERVAL = 5000

  connectedCallback () {
    // Find the tag and add the alert region if not present
    this.tag = this.querySelector('strong')
    if (!this.tag) return

    // Create alert region if it doesn't exist
    this.alert = this.querySelector('.status-checker__alert')
    if (!this.alert) {
      this.alert = document.createElement('span')
      this.alert.className = 'govuk-visually-hidden status-checker__alert'
      this.alert.setAttribute('aria-live', 'assertive')
      this.appendChild(this.alert)
    }

    // Set initial status color and start polling if needed
    this.#setStatusColour()
    const statusText = this.tag.textContent?.trim() || ''
    if (statusText !== 'completed' && statusText !== 'Complete' && statusText !== 'For review' && statusText !== 'failed') {
      this.polling = true
      this.#checkStatus()
    }
  }

  /**
   * Stop polling when the element is removed from the DOM.
   */
  disconnectedCallback () {
    this.polling = false
  }

  /**
   * Handle attribute changes.
   * @param { string } name
   * @param { string } oldValue
   * @param { string } newValue
   */
  attributeChangedCallback (name, oldValue, newValue) {
    // If job-id changes, restart polling
    if (name === 'job-id' && oldValue !== newValue && this.polling) {
      this.#checkStatus()
    }
    // If job-title changes, update alert text
    if (name === 'job-title' && this.alert) {
      this.#setAlert(this.tag?.textContent || '')
    }
  }

  /**
   * Check the job status from the backend and update the tag and alert.
   * @returns Promise<void>
   */
  async #checkStatus () {
    if (!this.polling || !this.tag || !this.getAttribute('job-id')) return

    const job = await StatusCache.checkStatus(this.getAttribute('job-id'))

    if (job) {
      const statusText = job.status || 'unknown'
      let displayText = statusText.replace('_', ' ')

      // If the status is completed, set the display text to "For review"
      if (statusText === 'completed') {
        displayText = 'For review'
      } else if (statusText === 'awaiting_start') {
        displayText = 'Queued'
      }

      this.tag.textContent = displayText
      this.#setAlert(displayText)
      this.#setStatusColour()
      this.setAttribute('status', statusText)

      // Add job name to the table row
      if (statusText === 'completed') {
        const nameField = this.closest('tr')?.querySelector('.extraction-list__job-name')
        if (nameField) {
          nameField.textContent = job.name || job.title || '-'
        }
      }
    }

    // Continue polling unless the job is completed or failed
    if (
      job &&
      job.status !== 'completed' &&
      job.status !== 'failed' &&
      this.polling
    ) {
      setTimeout(() => this.#checkStatus(), this.#CHECK_INTERVAL)
    } else if (this.polling && !job) {
      // If no job found (e.g., fetch failed), retry after interval
      setTimeout(() => this.#checkStatus(), this.#CHECK_INTERVAL)
    }
  }

  /**
   * Set the color of the status tag based on the current status.
   */
  #setStatusColour () {
    if (!this.tag) return
    const statusText = this.tag.textContent?.trim()
    this.tag.classList.remove(
      'govuk-tag--green',
      'govuk-tag--blue',
      'govuk-tag--red',
      'govuk-tag--grey'
    )
    if (statusText === 'completed' || statusText === 'Complete') {
      this.tag.classList.add('govuk-tag--green')
    } else if (statusText === 'For review') {
      this.tag.classList.add('govuk-tag--blue')
    } else if (statusText === 'failed') {
      this.tag.classList.add('govuk-tag--red')
    } else {
      this.tag.classList.add('govuk-tag--grey')
    }
  }

  /**
   * Set the alert text for screen readers.
   * @param { string } statusText The current status text to be announced.
   */
  #setAlert (statusText) {
    if (this.alert) {
      this.alert.textContent = `${
        this.getAttribute('job-title') || 'Job'
      } is ${statusText}`
    }
  }
}

customElements.define('status-checker', StatusChecker)

// Expose for testing
if (typeof window !== 'undefined') {
  window.StatusCache = StatusCache
}
